import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PanicOverlay from '../components/PanicOverlay';
import { PANIC_STAGE } from '../lib/panicStage';
import { usePanicSocket } from '../hooks/usePanicSocket';
import {
  acceptPanicIncident,
  closePanicIncident,
  endPanicTalk,
  fetchPanicIncidents,
  startPanicTalk,
} from '../api/panic';

const PanicResponseContext = createContext({
  attention: null,
  busy: false,
  attend: async () => null,
});

export const usePanicResponse = () => useContext(PanicResponseContext);

export const PanicResponseProvider = ({ enabled, userId, children }) => {
  const { t } = useTranslation();
  const [attention, setAttention] = useState(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  // Отложенные инциденты — на эту сессию: Оператор уже решил, что смотреть на них
  // не будет, и повторять ему это на каждом Срабатывании незачем.
  const setAsideRef = useRef(new Set());
  const attentionRef = useRef(null);
  useEffect(() => {
    attentionRef.current = attention;
  }, [attention]);

  const isMine = useCallback(
    (incident) => Boolean(userId) && incident?.accepted_by === userId,
    [userId]
  );

  /**
   * Возврат к Разговору всегда идёт через startTalk, а не через открытие плеера:
   * поток мог быть снят, пока Оператора не было (ADR-0006), и тогда плеер молча
   * подключился бы к пустоте.
   */
  const resumeConversation = useCallback(async (incident) => {
    await startPanicTalk(incident.id);
    setAttention({ incident, stage: PANIC_STAGE.CONVERSATION });
  }, []);

  // Перезагрузка страницы не отменяет тревогу: Оператор должен вернуться туда,
  // где его застали, а не в пустой журнал.
  useEffect(() => {
    if (!enabled) {
      setAttention(null);
      return undefined;
    }

    let cancelled = false;
    fetchPanicIncidents({ status: 'active', limit: 100 })
      .then(async ({ data }) => {
        if (cancelled || !Array.isArray(data) || attentionRef.current) return;

        const mine = data.find((incident) => incident.status === 'accepted' && isMine(incident));
        if (mine) {
          try {
            await resumeConversation(mine);
          } catch {
            if (!cancelled) setAttention({ incident: mine, stage: PANIC_STAGE.RESOLUTION });
          }
          return;
        }

        const ringing = data.find(
          (incident) => incident.status === 'open' && !setAsideRef.current.has(incident.id)
        );
        if (ringing && !cancelled) {
          setAttention({ incident: ringing, stage: PANIC_STAGE.RINGING });
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [enabled, isMine, resumeConversation]);

  const onIncident = useCallback((payload) => {
    if (!enabled || !payload?.id) return;

    // Журнал слушает то же событие: инцидент, сменивший руки, должен обновиться
    // в списке, даже если Оператор в этот момент занят другим.
    window.dispatchEvent(new CustomEvent('panicIncidentUpdated', { detail: payload }));

    const current = attentionRef.current;
    const isCurrent = current?.incident?.id === payload.id;

    if (payload.status === 'closed') {
      if (isCurrent) setAttention(null);
      return;
    }

    if (payload.status === 'accepted') {
      if (isMine(payload)) {
        if (isCurrent) {
          setAttention({ ...current, incident: { ...current.incident, ...payload } });
        }
        return;
      }
      if (payload.presence_lost) {
        setAttention({ incident: payload, stage: PANIC_STAGE.AWAY });
        return;
      }
      // Инцидент взял кто-то другой — на этом экране он больше не звонит.
      if (isCurrent && current.stage === PANIC_STAGE.RINGING) {
        setNotice(
          payload.accepted_by_email
            ? t('panic.takenBy', { who: payload.accepted_by_email })
            : t('panic.alreadyAccepted')
        );
        setAttention(null);
      }
      return;
    }

    if (payload.status === 'open') {
      if (setAsideRef.current.has(payload.id)) return;
      // Идущий Разговор нельзя перебивать чужой тревогой: она подождёт в журнале.
      if (current && current.stage !== PANIC_STAGE.RINGING) return;
      setAttention({ incident: payload, stage: PANIC_STAGE.RINGING });
    }
  }, [enabled, isMine, t]);

  usePanicSocket(enabled ? onIncident : null);

  const accept = useCallback(async (id) => {
    setAcceptLoading(true);
    try {
      const { incident } = await acceptPanicIncident(id);
      setAttention({ incident, stage: PANIC_STAGE.CONVERSATION });
      return incident;
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 409) {
        setNotice(
          data?.acceptedByEmail
            ? t('panic.takenBy', { who: data.acceptedByEmail })
            : (data?.message || t('panic.alreadyAccepted'))
        );
        setAsideRef.current.add(id);
        setAttention(null);
        return null;
      }

      // Разговор не поднялся, но инцидент уже за Оператором — значит, отвечать
      // за него всё равно ему, и Резолюция обязательна.
      if (status === 502 && data?.data) {
        setNotice(data.message || t('panic.talkError'));
        setAttention({ incident: data.data, stage: PANIC_STAGE.RESOLUTION });
        return data.data;
      }

      setNotice(err?.response?.data?.message || t('panic.talkError'));
      return null;
    } finally {
      setAcceptLoading(false);
    }
  }, [t]);

  // Трубку кладут, а инцидент остаётся: пока Резолюция не записана, разбор не окончен.
  const hangUp = useCallback(async () => {
    const current = attentionRef.current;
    if (!current) return;
    setActionLoading(true);
    try {
      await endPanicTalk(current.incident.id);
    } catch (err) {
      console.warn('[Panic] hang up failed', err);
    } finally {
      setActionLoading(false);
      setAttention({ incident: current.incident, stage: PANIC_STAGE.RESOLUTION });
    }
  }, []);

  const resolve = useCallback(async (resolution) => {
    const current = attentionRef.current;
    if (!current || !resolution?.trim()) return;
    setActionLoading(true);
    try {
      await closePanicIncident(current.incident.id, resolution.trim());
      setAttention(null);
    } catch (err) {
      setNotice(err?.response?.data?.message || t('panic.closeFailed'));
    } finally {
      setActionLoading(false);
    }
  }, [t]);

  const resume = useCallback(async () => {
    const current = attentionRef.current;
    if (!current) return;
    setActionLoading(true);
    try {
      await resumeConversation(current.incident);
    } catch (err) {
      setNotice(err?.response?.data?.message || t('panic.talkError'));
    } finally {
      setActionLoading(false);
    }
  }, [resumeConversation, t]);

  const setAside = useCallback(() => {
    const current = attentionRef.current;
    if (!current) return;
    setAsideRef.current.add(current.incident.id);
    setAttention(null);
  }, []);

  /**
   * Единственный вход в разбор со стороны журнала. Что именно произойдёт —
   * принятие или возврат к своему Разговору — решает состояние инцидента, а не
   * вызывающий: журналу незачем знать, чем принятие отличается от возврата.
   */
  const attend = useCallback(async (incident) => {
    if (incident?.status === 'open') {
      setAsideRef.current.delete(incident.id);
      return accept(incident.id);
    }
    if (incident?.status === 'accepted' && isMine(incident)) {
      setAsideRef.current.delete(incident.id);
      setActionLoading(true);
      try {
        await resumeConversation(incident);
        return incident;
      } catch (err) {
        setNotice(err?.response?.data?.message || t('panic.talkError'));
        return null;
      } finally {
        setActionLoading(false);
      }
    }
    return null;
  }, [accept, isMine, resumeConversation, t]);

  const value = useMemo(
    () => ({ attention, busy: acceptLoading || actionLoading, attend }),
    [attention, acceptLoading, actionLoading, attend]
  );

  return (
    <PanicResponseContext.Provider value={value}>
      {children}
      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[110] max-w-md -translate-x-1/2 rounded-2xl border border-amber-400/40 bg-surface-raised px-5 py-3 text-sm text-amber-100 shadow-xl">
          {notice}
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="ml-3 text-amber-200 underline"
          >
            {t('common.close')}
          </button>
        </div>
      )}
      {attention && (
        <PanicOverlay
          incident={attention.incident}
          stage={attention.stage}
          isMine={isMine(attention.incident)}
          acceptLoading={acceptLoading}
          actionLoading={actionLoading}
          onAccept={accept}
          onHangUp={hangUp}
          onResolve={resolve}
          onResume={resume}
          onSetAside={setAside}
        />
      )}
    </PanicResponseContext.Provider>
  );
};

export default PanicResponseContext;
